import React, { useEffect } from 'react';
import { Users, DollarSign, CreditCard, ArrowUpRight, ArrowDownRight, Activity, Zap, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLayout } from '../contexts/LayoutContext';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, colorClass }) => (
    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-soft hover:shadow-premium transition-all duration-300 group">
        <div className="flex justify-between items-start mb-4">
            <div className={`p-3 rounded-xl ${colorClass} bg-opacity-10 transition-colors group-hover:bg-opacity-20`}>
                <Icon className={`w-6 h-6 ${colorClass.replace('bg-', 'text-')}`} />
            </div>
            {trend && (
                <div className={`flex items-center gap-1 text-xs font-bold ${trend === 'up' ? 'text-emerald-500' : 'text-rose-500'}`}>
                    {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {trendValue}
                </div>
            )}
        </div>
        <div>
            <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
            <h3 className="text-2xl font-bold text-slate-900 tracking-tight">{value}</h3>
        </div>
    </div>
);

export default function Dashboard() {
    const { user } = useAuth();
    const { setTitle } = useLayout();

    useEffect(() => {
        setTitle('Resumen General');
    }, [setTitle]);

    return (
        <div className="space-y-8 animate-premium-in">
            {/* Header / Welcome Section */}
            <div className="relative overflow-hidden rounded-3xl premium-gradient p-8 md:p-12 text-white shadow-premium">
                <div className="relative z-10 max-w-2xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/10 text-xs font-bold uppercase tracking-wider mb-6">
                        <Zap className="w-3 h-3 text-emerald-300" />
                        <span>Resumen del Sistema</span>
                    </div>
                    <h1 className="text-3xl md:text-5xl font-extrabold mb-4 tracking-tight leading-tight">
                        ¡Hola de nuevo, <br />
                        <span className="text-emerald-400">{user?.nombre || 'Administrador'}!</span>
                    </h1>
                    <p className="text-emerald-50/80 text-lg leading-relaxed mb-0">
                        Aquí tienes un vistazo rápido al estado actual de la cooperativa y las tareas pendientes para hoy.
                    </p>
                </div>
                
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 right-1/4 w-32 h-32 bg-white/5 rounded-full blur-2xl"></div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                    title="Socios Activos" 
                    value="1,280" 
                    icon={Users} 
                    trend="up" 
                    trendValue="+12%" 
                    colorClass="bg-emerald-500" 
                />
                <StatCard 
                    title="Caja Disponible" 
                    value="₲ 45.2M" 
                    icon={DollarSign} 
                    trend="up" 
                    trendValue="+5.4k" 
                    colorClass="bg-blue-500" 
                />
                <StatCard 
                    title="Préstamos hoy" 
                    value="14" 
                    icon={CreditCard} 
                    trend="down" 
                    trendValue="-2" 
                    colorClass="bg-amber-500" 
                />
                <StatCard 
                    title="Nivel de Riesgo" 
                    value="Bajo (2.1%)" 
                    icon={ShieldCheck} 
                    colorClass="bg-indigo-500" 
                />
            </div>

            {/* Secondary Content Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Quick Shortcuts */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-3xl border border-slate-100 shadow-soft p-8 h-full">
                        <div className="flex items-center justify-between mb-8">
                            <h3 className="text-xl font-bold text-slate-900">Actividad Reciente</h3>
                            <button className="text-sm font-bold text-coop-600 hover:text-coop-700 transition-colors">Ver todo</button>
                        </div>
                        
                        <div className="space-y-6">
                            {[
                                { title: 'Nueva solicitud de crédito', time: '12 min ago', user: 'Carlos M.' },
                                { title: 'Arqueo de caja aprobado', time: '1h ago', user: 'Marta R.' },
                                { title: 'Socio dado de alta', time: '3h ago', user: 'Sistema' },
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-4 group">
                                    <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-coop-50 group-hover:text-coop-600 transition-all">
                                        <Activity className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-bold text-slate-800">{item.title}</p>
                                        <p className="text-xs text-slate-500">{item.user} • {item.time}</p>
                                    </div>
                                    <ArrowUpRight className="w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-all" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right Side Card: Status */}
                <div className="bg-slate-900 rounded-3xl p-8 text-white shadow-premium relative overflow-hidden">
                    <h3 className="text-xl font-bold mb-6 relative z-10">Estado del Sistema</h3>
                    <div className="space-y-6 relative z-10">
                        <div className="flex justify-between items-center">
                            <span className="text-slate-400 text-sm">Servidor API</span>
                            <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">ONLINE</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-slate-400 text-sm">Base de Datos</span>
                            <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-[10px] font-bold">CONECTADO</span>
                        </div>
                        <div className="pt-4 border-t border-white/10">
                            <p className="text-xs text-slate-500 mb-2 font-bold uppercase tracking-wider">Última Auditoría</p>
                            <p className="text-sm text-slate-300">Hace 45 minutos por admin</p>
                        </div>
                    </div>
                    {/* Decorative mask */}
                    <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-coop-600/20 rounded-full blur-2xl"></div>
                </div>
            </div>
        </div>
    );
}
